'use client';

import { useState } from 'react';
import { Share2, Copy, Check, Twitter, Facebook, Linkedin } from 'lucide-react';
import { Button } from '../ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { toast } from 'sonner';

/**
 * ShareButton component for sharing content on social media and copying links
 * @param {string} title - The title of the content to share
 * @param {string} description - A short description for social sharing
 * @param {string} url - The URL to share (defaults to current page)
 * @param {string} variant - Button variant (default, outline, ghost)
 * @param {string} size - Button size (sm, default, lg)
 * @param {boolean} showText - Whether to show "Share" text
 */
export function ShareButton({
  title,
  description,
  url,
  variant = 'outline',
  size = 'sm',
  showText = false,
  className = '',
}) {
  const [copied, setCopied] = useState(false);

  // Use provided URL or current page URL
  const shareUrl =
    url || (typeof window !== 'undefined' ? window.location.href : '');
  const encodedUrl = encodeURIComponent(shareUrl);
  const encodedTitle = encodeURIComponent(
    title || 'Check this out on Talktrail'
  );
  const encodedDescription = encodeURIComponent(
    description || 'Join the discussion on Talktrail - The Web3 Forum'
  );

  // Social share URLs
  const twitterUrl = `https://twitter.com/intent/tweet?text=${encodedTitle}&url=${encodedUrl}&via=Tracverse_`;
  const facebookUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}&quote=${encodedTitle}`;
  const linkedinUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`;

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      toast.success('Link copied to clipboard!');
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      toast.error('Failed to copy link');
    }
  };

  const handleNativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: title || 'Talktrail',
          text: description || 'Check this out on Talktrail',
          url: shareUrl,
        });
      } catch (err) {
        // User cancelled or error - silently ignore
        if (err.name !== 'AbortError') {
          console.error('Share failed:', err);
        }
      }
    }
  };

  const openShareWindow = (url) => {
    window.open(url, '_blank', 'width=600,height=400,menubar=no,toolbar=no');
  };

  // Check if native sharing is supported
  const hasNativeShare = typeof navigator !== 'undefined' && navigator.share;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant={variant}
          size={size}
          className={`gap-2 border-white/10 text-zinc-400 hover:text-white hover:bg-white/10 ${className}`}
        >
          <Share2 className="h-4 w-4" />
          {showText && <span>Share</span>}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        className="w-48 bg-zinc-900 border-zinc-800 text-white"
      >
        {hasNativeShare && (
          <>
            <DropdownMenuItem
              onClick={handleNativeShare}
              className="hover:bg-zinc-800 cursor-pointer"
            >
              <Share2 className="mr-2 h-4 w-4" />
              <span>Share...</span>
            </DropdownMenuItem>
            <DropdownMenuSeparator className="bg-zinc-800" />
          </>
        )}

        <DropdownMenuItem
          onClick={handleCopyLink}
          className="hover:bg-zinc-800 cursor-pointer"
        >
          {copied ? (
            <Check className="mr-2 h-4 w-4 text-green-500" />
          ) : (
            <Copy className="mr-2 h-4 w-4" />
          )}
          <span>{copied ? 'Copied!' : 'Copy link'}</span>
        </DropdownMenuItem>

        <DropdownMenuSeparator className="bg-zinc-800" />

        <DropdownMenuItem
          onClick={() => openShareWindow(twitterUrl)}
          className="hover:bg-zinc-800 cursor-pointer"
        >
          <Twitter className="mr-2 h-4 w-4" />
          <span>Share on X</span>
        </DropdownMenuItem>

        <DropdownMenuItem
          onClick={() => openShareWindow(facebookUrl)}
          className="hover:bg-zinc-800 cursor-pointer"
        >
          <Facebook className="mr-2 h-4 w-4" />
          <span>Share on Facebook</span>
        </DropdownMenuItem>

        <DropdownMenuItem
          onClick={() => openShareWindow(linkedinUrl)}
          className="hover:bg-zinc-800 cursor-pointer"
        >
          <Linkedin className="mr-2 h-4 w-4" />
          <span>Share on LinkedIn</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
